/**
 * Planner Agent
 * 
 * A conversational AI assistant that helps users create comprehensive presentation plans
 * through iterative collaboration before any slides are built.
 * 
 * Workflow (per Notion documentation):
 * 1. Analyze initial prompt for clarity, data needs, ambiguities
 * 2. Gather information through user interviews or research
 * 3. Create initial plan with logical section structure
 * 4. Iterate on plan based on user feedback
 * 5. Support version control and plan refinement
 * 
 * Current Implementation:
 * - Phase 2: User Interview Tool (userInterview)
 * - Phase 2: AlphaSense Research Tool (alphaSenseSearchAgent)
 * - Phase 3: Plan Creation (createPlan tool)
 * - Phase 4: Iteration & Refinement (editPlan tool)
 */

import { ToolDescription } from "../base/toolDescription";
import { AgentBaseClass } from "../base";
import { SubmitResultsParams } from "../base/submitResults.schema";
import { ChatMessage } from "../../globals/llm/baseAdapter";
import { ThreadId, ResultItem, ResolvedContextItem, CancellationToken, AgentCallbacks } from "../../core/types";
import { CreatePlanTool } from "./tools/createPlan";
import { EditPlanTool } from "./tools/editPlan";
import { UserInterviewTool } from "../tools/userInterview";
import { AlphaSenseAgent } from "../search";
import { PlannerParamsSchema, type PlannerParams, type PlannerResponse } from "./planner.schema";
import { Plan } from "./tools/types";
import { getPlannerVersionHistoryManager, type PlannerVersionHistoryManager } from "../../core/plannerVersionHistoryManager";
import { buildPlannerSystemPrompt, generateCurrentPlanContext, generateStyleGuideContext } from "./prompt";

// ═══════════════════════════════════════════════════════════════════════════
// PLANNER AGENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Planner agent that creates and modifies presentation plans
 * 
 * The planner:
 * - Receives user messages about presentation requirements
 * - Creates initial plans with sections and slides
 * - Iterates on plans based on user feedback
 * - Streams responses back to the UI
 * 
 * Uses the base class execute() method with customized hooks:
 * - buildInitialMessages(): Builds user message with context
 * - beforeExecute(): Resets tracking and sets current plan
 * - onToolResult(): Tracks plan changes
 * - buildFinalResult(): Returns PlannerResponse format
 * 
 * @example
 * const planner = new PlannerAgent();
 * const result = await planner.execute(
 *     { message: "Create a 15 slide presentation about Q4 revenue" },
 *     { onChunk: (chunk) => updateUI(chunk) }
 * );
 */
class PlannerAgent extends AgentBaseClass<PlannerParams, PlannerResponse> {

    /** Current plan being worked on */
    private currentPlan: Plan | null = null;

    /** Current job ID for version history tracking */
    private currentJobId: string | null = null;

    /** Version history manager for the current job */
    private versionHistoryManager: PlannerVersionHistoryManager | null = null;

    /** Track action type for response */
    private lastAction: 'plan_created' | 'plan_updated' | 'conversation' | 'error' = 'conversation';

    /** Track metadata for response */
    private lastMetadata: PlannerResponse['metadata'] = {};

    constructor() {
        super();
        this.name = "planner";

        // Use base class parameter validation
        this.paramsSchema = PlannerParamsSchema;

        // Disable infrastructure not needed for planner
        this.useTaskTracking = false;
        this.useThreadManagement = false;
        this.useContextResolution = false;
        this.createsUndoCheckpoint = false; // Planner doesn't modify presentation

        this.toolCalls = [
            new CreatePlanTool(),
            new EditPlanTool(),
            new UserInterviewTool(),
            // Research agents (Agent-as-Tool pattern)
            //new AlphaSenseAgent(),
        ];
        this.description = new ToolDescription(
            "planner",
            "Conversational AI assistant that creates comprehensive presentation plans through iterative collaboration. Helps structure presentations with logical sections and slides before building.",
            PlannerParamsSchema,
            {
                // TODO: Fill in systemPromptUsage
                briefDescription: "",
                whenToUse: [],
                exampleUsage: "",
                commonMistakes: [],
                capabilities: [],
            }
        );

        // Default to streaming mode
        this.stream = true;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // REMINDER OPTIONS - Disable PowerPoint context for dialog
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Override reminder options to disable presentation context collection.
     * The Planner Agent runs in a dialog context which doesn't have access
     * to PowerPoint.run() APIs, so we skip presentation context to avoid errors.
     */
    protected getReminderOptions(): {
        includePresentation?: boolean;
        includeCustomInstructions?: boolean;
        includeSecurityWarning?: boolean;
        includeDeckRegistry?: boolean;
        anchoreddocumentId?: string;
    } {
        return {
            includePresentation: false,  // Disabled - dialog context can't access PowerPoint APIs
            includeSecurityWarning: false,  // Not needed for planning
            includeCustomInstructions: false,
            includeDeckRegistry: false,  // Not needed for planning
        };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Set the current plan (used when editing existing plans)
     */
    public setPlan(plan: Plan | null): void {
        this.currentPlan = plan;
    }

    /**
     * Get the current plan
     */
    public getPlan(): Plan | null {
        return this.currentPlan;
    }

    /**
     * Set streaming mode
     */
    public setStreaming(enabled: boolean): void {
        this.stream = enabled;
    }

    /**
     * Check if streaming is enabled
     */
    public isStreaming(): boolean {
        return this.stream;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // HOOKS - Customize base class behavior
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Generate the system prompt for the planner
     * Uses the new standardized 5-section prompt structure
     */
    public generateSystemPrompt(_resolvedContext?: ResolvedContextItem[]): string {
        const basePrompt = buildPlannerSystemPrompt(this.toolCalls);

        // Add current plan context if it exists - include full plan JSON so LLM knows exact IDs
        if (this.currentPlan) {
            return basePrompt + generateCurrentPlanContext(this.currentPlan);
        }

        return basePrompt;
    }

    /**
     * Build initial messages from the user's initialPrompt parameter
     */
    protected buildInitialMessages(params: PlannerParams, _resolvedContext?: ResolvedContextItem[]): ChatMessage[] {
        console.log('[PlannerAgent] buildInitialMessages called with params:', {
            hasInitialPrompt: !!params.initialPrompt,
            slideCount: params.slideCount,
            styleGuideType: params.styleGuide?.type,
            styleGuidePath: params.styleGuide?.path,
            hasParsedStyleGuide: !!params.parsedStyleGuide,
            parsedStyleGuideSlideCount: params.parsedStyleGuide?.slides?.length,
        });

        let userMessage = params.initialPrompt;

        // Add slide count context if no current plan
        if (!this.currentPlan) {
            userMessage = `Target slide count: ${params.slideCount}\n\n${userMessage}`;
        }

        // Add style guide context
        if (params.styleGuide.type === 'live') {
            userMessage += '\n\nNote: Use the active presentation as a style guide.';
        } else if (params.styleGuide.type === 'uploaded' && params.styleGuide.path) {
            userMessage += `\n\nNote: Use uploaded presentation as a style guide.`;
        }

        // Add parsed style guide context if available
        if (params.parsedStyleGuide) {
            console.log('[PlannerAgent] Adding parsed style guide context:', {
                slideCount: params.parsedStyleGuide.slides?.length || 0,
                layoutsUsed: params.parsedStyleGuide.layouts_used?.length || 0,
                sections: params.parsedStyleGuide.sections?.length || 0,
            });
            const styleGuideContext = generateStyleGuideContext(params.parsedStyleGuide);
            console.log('[PlannerAgent] Generated style guide context length:', styleGuideContext.length);
            console.log('[PlannerAgent] Style guide context preview:', styleGuideContext.substring(0, 500) + '...');
            userMessage += styleGuideContext;
        } else {
            console.log('[PlannerAgent] No parsed style guide provided in params');
        }

        console.log('[PlannerAgent] Final user message length:', userMessage.length);

        return [{
            role: 'user' as const,
            content: userMessage,
        }];
    }

    /**
     * Hook called before execution starts - reset tracking and set current plan
     */
    protected async beforeExecute(context: {
        params: PlannerParams;
        threadId: ThreadId;
        taskId: string;
    }): Promise<void> {
        // Reset tracking for this execution
        this.lastAction = 'conversation';
        this.lastMetadata = {};

        // Set current plan if provided in params
        if (context.params.currentPlan) {
            this.currentPlan = context.params.currentPlan;
        }

        // Initialize version history manager if jobId is provided
        if (context.params.jobId) {
            this.currentJobId = context.params.jobId;
            this.versionHistoryManager = getPlannerVersionHistoryManager(context.params.jobId);
            console.log(`[PlannerAgent] Initialized version history for job ${context.params.jobId}`);
        }
    }

    /**
     * Hook called when a tool returns a result - track plan changes and create versions
     */
    protected onToolResult(toolName: string, result: unknown): void {
        // Handle createPlan result
        if (toolName === 'createPlan') {
            const createResult = result as { success: boolean; plan: Plan; message: string; metadata?: any };
            if (createResult.success) {
                this.currentPlan = createResult.plan;
                this.lastAction = 'plan_created';
                this.lastMetadata = {
                    totalSlides: createResult.plan.totalSlides,
                    totalSections: createResult.plan.totalSections
                };

                // Create version in history
                if (this.versionHistoryManager) {
                    const totalSlides = createResult.plan.totalSlides || 0;
                    const totalSections = createResult.plan.totalSections || 0;
                    this.versionHistoryManager.createVersion(
                        createResult.plan,
                        'ai',
                        `Created initial plan with ${totalSections} sections and ${totalSlides} slides`
                    );
                }
            }
        }

        // Handle editPlan result
        if (toolName === 'editPlan') {
            const editResult = result as { success: boolean; plan: Plan; changes: any; message: string };
            if (editResult.success) {
                this.currentPlan = editResult.plan;
                this.lastAction = 'plan_updated';
                this.lastMetadata = {
                    totalSlides: editResult.plan.totalSlides,
                    totalSections: editResult.plan.totalSections,
                    changes: editResult.changes
                };

                // Create version in history
                if (this.versionHistoryManager) {
                    // Build a summary from the changes
                    const changes = editResult.changes || {};
                    const changesSummary = this.buildChangesSummary(changes);
                    this.versionHistoryManager.createVersion(
                        editResult.plan,
                        'ai',
                        changesSummary
                    );
                }
            }
        }
    }

    /**
     * Build a human-readable summary of changes for version history
     */
    private buildChangesSummary(changes: {
        added?: string[];
        removed?: string[];
        modified?: string[];
        reordered?: string[];
    }): string {
        const parts: string[] = [];

        if (changes.added && changes.added.length > 0) {
            parts.push(`Added ${changes.added.length} item(s)`);
        }
        if (changes.removed && changes.removed.length > 0) {
            parts.push(`Removed ${changes.removed.length} item(s)`);
        }
        if (changes.modified && changes.modified.length > 0) {
            parts.push(`Modified ${changes.modified.length} item(s)`);
        }
        if (changes.reordered && changes.reordered.length > 0) {
            parts.push(`Reordered ${changes.reordered.length} item(s)`);
        }

        return parts.length > 0 ? parts.join(', ') : 'Plan updated';
    }

    /**
     * Build the planner-specific response format
     */
    protected buildFinalResult(
        _results: ResultItem<unknown>[],
        _threadId: ThreadId,
        _tokensUsed?: number,
        _summary?: string
    ): PlannerResponse {
        // Get the content from the last loop result
        const content = this.lastLoopResult?.content || '';

        return {
            plan: this.currentPlan || undefined,
            message: content,
            action: this.lastAction,
            metadata: this.lastMetadata
        };
    }

    /**
     * Process submitted results (required abstract method).
     * 
     * Planner does not use the Submit Results Pattern - it's a conversational
     * agent that streams responses directly to the user.
     * This method will not be called in normal operation.
     */
    protected async processSubmittedResults(
        _submittedResults: SubmitResultsParams,
        _params: PlannerParams,
        _context: {
            params: PlannerParams;
            threadId: ThreadId;
            taskId: string;
            cancellationToken: CancellationToken;
            resolvedContext: ResolvedContextItem[];
            conversationId: string;
            documentId: string;
            callbacks?: AgentCallbacks;
        }
    ): Promise<ResultItem<unknown>[]> {
        // Planner doesn't use Submit Results Pattern - this won't be called
        throw new Error('Planner does not use Submit Results Pattern');
    }
}

export default PlannerAgent;
export { PlannerAgent, PlannerParamsSchema };
export type { PlannerParams, PlannerResponse };

